/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { useEffect, useRef, useState } from 'react';
import type { FunctionComponent } from 'react';
import type Teacher from '@whizz/api-client/dist/@types/model/Teacher';
import type { MessageStudentsProps as MessageStudentsType } from './modules/MessageStudents';
import type { GroupReportInterface } from '@whizz/api-model';
import type { RecentDateOptionsType } from '../types';
import styled from 'styled-components';
import { useMixpanel } from 'react-mixpanel-browser';
import { useI18n } from '@whizz/react-i18n';
import { useIdentity } from '@whizz/react-identity';
import { repository } from '@whizz/api-client';
import { useClassSelector, useSaveClass } from '../../hooks/filters/useClassSelector';
import { useRecentDateSelector } from '../../hooks/filters/useRecentDateSelector';
import { useConfetti } from '../../hooks/useConfetti';
import { useMock } from '../../hooks/useMock';
import { HomeLayout } from '../../layouts';
import WeeklyEmailToggle from './components/WeeklyEmailToggle';
import { GetYourStudentsStarted, HallOfFame, LastWeeksData, RecentData, StudentInsight, YourStudents } from './sections';
import { Alert, Collapse } from '../../components';
import { ErrorMessage  } from './modules';
import { BUTTON_WRAPPER } from '../../util/data-id';
import { useQuery } from 'react-query';
import { PRODUCT_FRUITS_TOURS, QUERIES_IDS, ROUTES } from '../../util/vars';
import { useHistory } from 'react-router-dom';
import { DateTime } from 'luxon';
import { useLocalStorage } from 'react-use';
import { mapAllGroupReportPosNeg } from '../../mapper/groupMapper';
import NoClasses from '../../global/sections/NoClasses';
import { getAnimationDurationAsInt } from '../../util/styled';
import classNames from 'classnames';
import { isProductFruitsReady, useProductFruitsApi } from 'react-product-fruits';
import { useMessages } from '../../hooks/useMessages';
import { LoginDetails } from './sections/LoginDetails';

const AUTO_REFETCH_INTERVAL = 60000;
const OPEN_DELAY = getAnimationDurationAsInt() * 1.5;

type Sections = 'initial' | 'get_started' | 'login_details' | 'this_week' | 'help' | 'hall_of_fame' | 'last_x_weeks' | 'student';
type ActualSections = Exclude<Sections, 'initial'>;

export interface SectionProps {
  isLoading: boolean;
  groupReport: GroupReportInterface | null;
  open: boolean;
  setOpen: (section?: Sections) => void;
}

const openSectionsForTourWhenCardEnds: Record<string, ActualSections> = {
  '107d4731-bdae-452d-9c94-46ee607f53cf': 'get_started',
  'd1096c7a-97ad-455f-83fa-10d199a03081': 'this_week',
  '3c570848-ffc9-43d0-baee-186b20dee6eb': 'help',
  '433f8de2-d2c4-47e2-90d4-d78919643635': 'hall_of_fame',
  'd2fe8a76-6055-420a-99c7-18ecaef638de': 'last_x_weeks',
  '65a84a5b-6ee4-48d7-987e-91e37f6568a8': 'student',
  '78d3b3de-aed4-48d7-888f-073f69ec601d': 'login_details',
};

const openSectionsForTour: Record<ActualSections, string[]> = {
  'get_started': [ '107d4731-bdae-452d-9c94-46ee607f53cf', 'b2d202f0-2555-4248-b5b2-9bc9ea6f7e4c', '04f38a00-a448-462c-949e-15a499aedc6f',  'd1096c7a-97ad-455f-83fa-10d199a03081' ],
  'this_week': [ '838c6c16-281d-4be7-afc7-9f308eb637fc', 'a802cc1a-2471-44eb-b1f7-27fc07cb929a', '194d7bbc-25ae-4771-b675-75df0ce9fa83', '3c570848-ffc9-43d0-baee-186b20dee6eb' ],
  'help': [ '433f8de2-d2c4-47e2-90d4-d78919643635' ],
  'hall_of_fame': [ 'd2fe8a76-6055-420a-99c7-18ecaef638de' ],
  'last_x_weeks': [ '65a84a5b-6ee4-48d7-987e-91e37f6568a8' ],
  'student': [ '8eb62279-5e94-4020-82cd-83552b9db715' ],
  'login_details': [ '78d3b3de-aed4-48d7-888f-073f69ec601d' ],
};

const Home: FunctionComponent = () => {
  const mixpanel = useMixpanel();
  const history = useHistory();
  const { me } = useIdentity();
  const { translate } = useI18n();

  const [ open, setOpen ] = useState<Sections | null>('initial');

  const teacherId = (me as Teacher).getId();

  const passwordUpdateQuery = useQuery<boolean>(
    [ QUERIES_IDS.PASSWORD_INITIALISATION, 'firstLogin' ],
    () => repository.NewTeacherRepository.isPasswordUpdateNeeded(),
    {
      enabled: true,
    },
  );

  const isPasswordUpdateNeeded = passwordUpdateQuery.data;
  const goToPasswordInitialisationPage = () => history.push(ROUTES.password_initialisation);

  const currentDateValue = useRef<RecentDateOptionsType | null>(null);
  const [ recentDateSelector, dateValue, customDateValue, autoFetch ] = useRecentDateSelector(me?.username ?? '');
  const [ confetti, toggleConfetti ] = useConfetti();
  const [ modal, setMessageStudents ] = useMessages();

  const [ savedClass ] = useSaveClass(me?.username as string);

  const classesQuery = useQuery(
    [ QUERIES_IDS.CLASSES, teacherId ],
    () => repository.NewTeacherRepository.getClasses(teacherId!),
    {
      enabled: null != teacherId && null != savedClass?.value,
      placeholderData: [],
    },
  );

  const [ classSelector, classValue ] = useClassSelector(me?.username as string, classesQuery.data!, 'center', 'sm');

  const lsKey = `got_it_${teacherId}`;
  const [ showGetStarted, setShowGetStarted ] = useLocalStorage(`${lsKey}${classValue?.value ? `_${classValue?.value}` : ''}`, true);
  if (window.localStorage.getItem(lsKey)) {
    // Hacky, but required to prevent creating unecessary localStorage entries. As hooks cannot be created conditionally one is created without a classValue if no class is selected. This needs removing.
    window.localStorage.removeItem(lsKey);
  }

  const [ mock ] = useMock();

  const groupReportQuery = useQuery<GroupReportInterface | null>(
    [ QUERIES_IDS.HOME_GROUP_REPORT, teacherId, classValue?.value, dateValue?.value, customDateValue?.value ],
    ({ signal }) => repository.GroupReportRepository.oneByGroupId(classValue!.value, {
      async: true,
      signal,
      recentDate: calculateRecentDate(),
      ...mock,
    }),
    {
      enabled: null != classValue?.value && (dateValue?.value !== 'since_x' || !!customDateValue?.value),
      refetchOnWindowFocus: autoFetch,
      refetchOnReconnect: autoFetch,
      retry: false,
      placeholderData: null,
      refetchInterval: autoFetch ? AUTO_REFETCH_INTERVAL : false,
      onSuccess: () => {
        if (dateValue?.value !== undefined) {
          currentDateValue.current = dateValue?.value; //Store the dateValue to determine if this has changed
        }
      },
    },
  );

  const calculateRecentDate = () => {
    let recentDate = DateTime.now().startOf('day');
    if (dateValue?.value === 'since_x' && customDateValue?.value) {
      const targetDay = Number(customDateValue.value.slice(-1));

      if (targetDay > recentDate.weekday) {
        // targetDay is in a different week
        recentDate = recentDate.minus({ weeks: 1 });
      }
      recentDate = recentDate.set({ weekday: targetDay });
    } else if (dateValue?.value === 'today') {
      return recentDate;
    } else {
      recentDate = recentDate.minus({ weeks: 1 }).plus({ days: 1 });
    }
    return recentDate;
  };

  const showMessageStudents = (students: MessageStudentsType['students'], type: MessageStudentsType['type']) => {
    setMessageStudents({ students, type });
  };

  useEffect(() => {
    if (isPasswordUpdateNeeded) {
      goToPasswordInitialisationPage();
    } else if (!savedClass?.value && passwordUpdateQuery.isFetched) {
      history.push(ROUTES.welcome);
    }
  }, [ isPasswordUpdateNeeded, passwordUpdateQuery.isFetched, savedClass?.value ]);

  useEffect(() => {
    if (!mixpanel || !mixpanel?.config?.token) {
      return;
    }
    mixpanel.track('Teacher Feed');
  }, [ mixpanel ]);

  useEffect(() => {
    if (groupReportQuery.data) {
      const data = mapAllGroupReportPosNeg(groupReportQuery.data);
      if (data.negative.length > 0) {
        setShowGetStarted(true);
      }
    }
  }, [ groupReportQuery.data ]);

  useEffect(() => {
    if (classValue?.value !== null) {
      setOpen(showGetStarted ? 'get_started' : 'initial');
    }
  }, [ classValue?.value ]);

  useProductFruitsApi((api) => {
    if (isProductFruitsReady()) {
      /**
       * Both tour-card-completed and tour-advanced are needed to workaround scrolling issues, especially if the component opening is long.
       */
      api.tours.listen('tour-card-completed', (tourId: number, { currentCardId }: { currentCardId: string }) => {
        if ([ PRODUCT_FRUITS_TOURS.teacherFeed, PRODUCT_FRUITS_TOURS.loginDetails ].includes(tourId)) {
          const openSection = openSectionsForTourWhenCardEnds[currentCardId];
          if (openSection) {
            pfToggle(openSection);
          }
        }
      });
      api.tours.listen('tour-advanced', (tourId: number, { currentCardId }: { currentCardId: string }) => {
        if ([ PRODUCT_FRUITS_TOURS.teacherFeed, PRODUCT_FRUITS_TOURS.loginDetails ].includes(tourId)) {
          let openSection: ActualSections | null = null;
          Object.keys(openSectionsForTour).forEach((key) => {
            const findId = openSectionsForTour[key as ActualSections].includes(currentCardId);
            if (findId) {
              openSection = key as ActualSections;
            }
          });
          if (openSection) {
            pfToggle(openSection);
          }
        }
      });
      api.tours.listen('tour-finished', (tourId: number) => {
        if ([ PRODUCT_FRUITS_TOURS.teacherFeed ].includes(tourId)) {
          pfToggle('get_started');
        }
      });
    }
  }, [ ]);

  const pfToggle = (section: Sections) => {
    if (section !== open) {
      toggleOpen(section)(section);
    }
  };

  const toggleOpen = (section: Sections) => (forceSection?: Sections) => {
    const isOpen = open;
    const showSection = forceSection ? forceSection : open !== section ? section : null;

    if (isOpen && isOpen !== 'initial') {
      setOpen(null);
      if (showSection) {
        setTimeout(() => setOpen(showSection), OPEN_DELAY);
      }
    } else {
      setOpen(showSection);
    }
  };

  const hasStudents = !!groupReportQuery.data?.students.length;
  const isLoading = !hasStudents && (!groupReportQuery.isFetched || groupReportQuery.isFetching);
  const nudgeShake = !showGetStarted && (open === 'initial' || open === 'get_started');
  const accessCode = classesQuery.data?.find((c) => c.id === classValue?.value)?.accessCode;
  const showNoClasses = classesQuery.isFetched && 0 === classesQuery.data?.length;

  const isResolvingDestination = !passwordUpdateQuery.isFetched || !savedClass?.value;

  return (
    <HomeLayout
      actionContent={isResolvingDestination ? null : <ClassSelectorStyled>{classSelector}</ClassSelectorStyled>}
      subActionContent={classValue && hasStudents ? <WeeklyEmailToggle classId={classValue?.value} /> : undefined}
      isLoading={isResolvingDestination || (null != classValue && isLoading)}
      classId={classValue?.value}
    >
      <ErrorMessage />
      <Alert show={groupReportQuery.isError} type={'error'}>{translate('global.API_ERROR')}</Alert>
      {!classValue && (
        showNoClasses
          ? <NoClasses />
          : <Alert show>{translate('pages.HOME.SELECT_CLASS')}</Alert>)}
      {classValue && (
        <>
          {modal}
          <div data-cy={hasStudents ? 'home_has_students' : 'home_no_students'}>
            <Collapse when={!!showGetStarted}>
              <GetYourStudentsStarted
                isLoading={isLoading}
                isVisible={!!showGetStarted}
                groupReport={groupReportQuery.data!}
                confetti={confetti}
                onGotIt={() => {
                  toggleConfetti(true);
                  setShowGetStarted(false);
                }}
                onMessageStudents={showMessageStudents}
                open={open === 'get_started'}
                setOpen={toggleOpen('get_started')}
              />
            </Collapse>
            {(groupReportQuery.data?.students.length !== 0) && (
              <div className={`space-y-6 ${classNames({ 'mt-6' : showGetStarted })}`}>
                <LoginDetails
                  accessCode={accessCode ?? null}
                  classValue={classValue}
                  data-cy="section-loginDetails"
                  open={open === 'login_details'}
                  setOpen={toggleOpen('login_details')}
                />
                <RecentData
                  isLoading={isLoading}
                  groupReport={groupReportQuery.data!}
                  onMessageStudents={showMessageStudents}
                  dateSelector={recentDateSelector}
                  data-cy="section-recent"
                  open={open === 'this_week'}
                  setOpen={toggleOpen('this_week')}
                  shake={nudgeShake}
                />
                <YourStudents
                  isLoading={isLoading}
                  groupReport={groupReportQuery.data!}
                  groupId={classValue!.value}
                  data-cy="section-yourStudents"
                  open={open === 'help'}
                  setOpen={toggleOpen('help')}
                />
                <HallOfFame
                  isLoading={isLoading}
                  groupReport={groupReportQuery.data!}
                  onMessageStudents={showMessageStudents}
                  data-cy="section-hallOfFame"
                  open={open === 'hall_of_fame'}
                  setOpen={toggleOpen('hall_of_fame')}
                />
                <LastWeeksData
                  isLoading={isLoading}
                  groupReport={groupReportQuery.data!}
                  data-cy="section-lastWeeks"
                  open={open === 'last_x_weeks'}
                  setOpen={toggleOpen('last_x_weeks')}
                />
                <StudentInsight
                  isLoading={isLoading}
                  groupReport={groupReportQuery.data!}
                  sinceDate={customDateValue?.label ?? dateValue?.label ?? ''}
                  data-cy="section-studentInsight"
                  open={open === 'student'}
                  setOpen={toggleOpen('student')}
                />
              </div>
            )}
          </div>
        </>
      )}
    </HomeLayout>
  );
};

// Let's do it like this as it's an edge case:
const ClassSelectorStyled = styled.div`
    width: 100%;
    [data-id='${BUTTON_WRAPPER}'] {
      &:hover {
        background: rgba(255,255,255,0.8)!important;
      }
    }
  `;

export default Home;
